const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://wms-stg.justdeliveries.ai/api";

const getToken = () =>
  localStorage.getItem("authToken") ||
  sessionStorage.getItem("authToken") ||
  localStorage.getItem("token") ||
  sessionStorage.getItem("token") ||
  localStorage.getItem("accessToken") ||
  sessionStorage.getItem("accessToken");

const getRefreshToken = () =>
  localStorage.getItem("refreshToken") ||
  sessionStorage.getItem("refreshToken");

const setAccessToken = (token) => {
  if (!token) return;
  if (sessionStorage.getItem("authToken") != null) {
    sessionStorage.setItem("authToken", token);
  } else if (sessionStorage.getItem("token") != null) {
    sessionStorage.setItem("token", token);
  } else if (sessionStorage.getItem("accessToken") != null) {
    sessionStorage.setItem("accessToken", token);
  } else if (localStorage.getItem("authToken") != null) {
    localStorage.setItem("authToken", token);
  } else if (localStorage.getItem("token") != null) {
    localStorage.setItem("token", token);
  } else if (localStorage.getItem("accessToken") != null) {
    localStorage.setItem("accessToken", token);
  } else {
    localStorage.setItem("authToken", token);
  }
};

const setRefreshToken = (token) => {
  if (!token) return;
  if (sessionStorage.getItem("refreshToken") != null) {
    sessionStorage.setItem("refreshToken", token);
  } else if (localStorage.getItem("refreshToken") != null) {
    localStorage.setItem("refreshToken", token);
  } else {
    localStorage.setItem("refreshToken", token);
  }
};

const clearAuthStorage = () => {
  const keys = [
    "authToken",
    "token",
    "accessToken",
    "refreshToken",
    "isAuthenticated",
    "userCode",
    "warehouseId",
    "warehouseName",
  ];
  keys.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

const redirectToLogin = () => {
  if (typeof window === "undefined") return;
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
};

const authHeaders = (extra = {}) => {
  const token = getToken();
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const REFRESH_URLS = [`${API_BASE_URL}/login/refresh`, `${API_BASE_URL}/admin/refresh`];

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

const requestNewAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("Missing refresh token");

  let lastError = null;
  for (const refreshUrl of REFRESH_URLS) {
    try {
      const refreshRes = await fetch(refreshUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!refreshRes.ok) {
        if (refreshRes.status === 404) {
          continue;
        }
        const data = await refreshRes.json().catch(() => ({}));
        throw new Error(data.message || data.error || "Refresh token failed");
      }
      const data = await refreshRes.json().catch(() => ({}));
      const newAccessToken = data.token || data.accessToken || data.authToken;
      const newRefreshToken = data.refreshToken;
      if (!newAccessToken) throw new Error("Refresh response missing access token");
      setAccessToken(newAccessToken);
      if (newRefreshToken) setRefreshToken(newRefreshToken);
      return newAccessToken;
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error("Unable to refresh token");
};

const shouldSkipRefresh = (url) => {
  const normalizedUrl = String(url || "");
  return (
    normalizedUrl.includes("/login") ||
    normalizedUrl.includes("/refresh") ||
    normalizedUrl.includes("/reset")
  );
};

const authFetch = async (url, options = {}) => {
  const requestOptions = {
    ...options,
    headers: authHeaders(options.headers || {}),
  };

  const response = await fetch(url, requestOptions);
  if (response.status !== 401 || options._retry || shouldSkipRefresh(url)) {
    return response;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAuthStorage();
    redirectToLogin();
    return response;
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then((newToken) => {
        const retryHeaders = {
          ...(options.headers || {}),
          Authorization: `Bearer ${newToken}`,
        };
        return fetch(url, { ...options, _retry: true, headers: retryHeaders });
      });
  }

  isRefreshing = true;
  try {
    const newToken = await requestNewAccessToken();
    processQueue(null, newToken);
    const retryHeaders = {
      ...(options.headers || {}),
      Authorization: `Bearer ${newToken}`,
    };
    return await fetch(url, { ...options, _retry: true, headers: retryHeaders });
  } catch (err) {
    processQueue(err, null);
    clearAuthStorage();
    redirectToLogin();
    return response;
  } finally {
    isRefreshing = false;
  }
};
// Login Services
export const loginService = {
  login: (data) =>
   authFetch(`${API_BASE_URL}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.message || responseData.error || "Login failed",
        );
      }
      return responseData;
    }),

  resetPassword: (data) =>
   authFetch(`${API_BASE_URL}/login/reset`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.message || responseData.error || "Password reset failed",
        );
      }
      return responseData;
    }),
};

// Warehouse (current logged-in warehouse details)
export const warehouseService = {
  getMe: () =>
    authFetch(`${API_BASE_URL}/warehouse/me`).then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || "Failed to fetch warehouse");
      return data;
    }),
};

// Company Services
export const companyService = {
  createCompany: (data) =>
   authFetch(`${API_BASE_URL}/company`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.message || responseData.error || "Failed to create company",
        );
      }
      return responseData;
    }),

  getCompanies: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`${API_BASE_URL}/company/get-companies${query ? `?${query}` : ''}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }).then(async (res) => {
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || data.error || "Failed to fetch companies");
      }
      return data;
    });
  },

  // NEW: fetch single company by ID (for details view)
  getCompanyById: (id) =>
   authFetch(`${API_BASE_URL}/company/${id}`).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.message || data.error || "Failed to load company details",
        );
      }
      return data;
    }),

  updateCompany: (id, data) =>
   authFetch(`${API_BASE_URL}/company/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.message || responseData.error || "Failed to update company",
        );
      }
      return responseData;
    }),

  deleteCompany: (id) =>
   authFetch(`${API_BASE_URL}/company/${id}`, { method: "DELETE" }).then(
      async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || data.error || "Failed to delete company");
        return data;
      }
    ),
};

// Product Services
export const productService = {
  createProduct: (data) =>
   authFetch(`${API_BASE_URL}/products`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const json = await res.json();
      if (!res.ok) {
        const err = new Error(json.message || json.error || "Failed to create product");
        err.response = { data: json };
        throw err;
      }
      return json;
    }),

  getProducts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`${API_BASE_URL}/products${query ? `?${query}` : ''}`).then((res) => res.json());
  },

  getProductById: (id) =>
   authFetch(`${API_BASE_URL}/products/${id}`).then((res) => res.json()),

  updateProduct: (id, data) =>
   authFetch(`${API_BASE_URL}/products/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || json.message || "Failed to update product");
      return json;
    }),

  getProductInventory: (id) =>
   authFetch(`${API_BASE_URL}/products/${id}/inventory`).then((res) => res.json()),

  deleteProduct: (id) =>
   authFetch(`${API_BASE_URL}/products/${id}`, { method: "DELETE" }).then(
      async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Failed to delete product");
        return data;
      }
    ),

  bulkUploadProductExcel: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return authFetch(`${API_BASE_URL}/products/bulk-upload`, {
      method: "POST",
      body: formData,
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data.error || (Array.isArray(data.details) ? data.details.join("; ") : "Upload failed"));
        err.response = { data };
        throw err;
      }
      return data;
    });
  },
};

// Inward Services
export const inwardService = {
  createInwardTransaction: (data) =>
   authFetch(`${API_BASE_URL}/inward`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to create inward transaction",
        );
      }
      return responseData;
    }),

  getInwardTransactions: (filters) => {
    const params = new URLSearchParams(filters).toString();
    return authFetch(`${API_BASE_URL}/inward?${params}`).then((res) => res.json());
  },

  // OLD: getInwardTransactionById did not surface backend errors explicitly
  // getInwardTransactionById: (id) =>
  //  authFetch(`${API_BASE_URL}/inward/${id}`).then((res) => res.json()),

  // NEW: surface backend error message so edit flow can show a clear toast
  getInwardTransactionById: (id) =>
   authFetch(`${API_BASE_URL}/inward/${id}`).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.message || data.error || "Failed to load inward transaction",
        );
      }
      return data;
    }),

  bulkUploadInwardExcel: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return authFetch(`${API_BASE_URL}/inward/bulk-upload`, {
      method: "POST",
      body: formData,
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // If response has errors array, include it in the error
        const errorObj = new Error(data.error || (Array.isArray(data.details) ? data.details.join("; ") : "Upload failed"));
        errorObj.responseData = data; // Attach full response data
        throw errorObj;
      }
      return data;
    });
  },

  updateInwardTransaction: (id, data) =>
   authFetch(`${API_BASE_URL}/inward/${id}/edit`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to update inward transaction",
        );
      }
      return responseData;
    }),

  updateInwardStatus: (id, data) =>
   authFetch(`${API_BASE_URL}/inward/${id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to update inward status",
        );
      }
      return responseData;
    }),

  getInwardHistoryReport: (filters) => {
    const params = new URLSearchParams(filters).toString();
    return authFetch(`${API_BASE_URL}/reports/inward-history?${params}`).then(
      (res) => res.json(),
    );
  },

  deleteInwardTransaction: (id) =>
   authFetch(`${API_BASE_URL}/inward/${id}`, { method: "DELETE" }).then(
      async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Failed to delete inward transaction");
        return data;
      }
    ),

  downloadInwardGRN: async (id) => {
    const res = await authFetch(`${API_BASE_URL}/inward/${id}/grn`);
    if (!res.ok) throw new Error("Failed to download GRN");
    return res.blob();
  },

  downloadInwardExcel: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/reports/inward-download${query ? `?${query}` : ""}`;
    console.log("FINAL URL:", url);
    const res = await authFetch(url, {
      method: "GET",
    });
    if (!res.ok) throw new Error("Failed to export inward excel");
    return res.blob();
  },
};

// Outward Services
export const outwardService = {
  createOutwardTransaction: (data) =>
   authFetch(`${API_BASE_URL}/outward`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        const err = new Error(
          responseData.error || "Failed to create outward transaction",
        );
        if (responseData.details) err.details = responseData.details;
        throw err;
      }
      return responseData;
    }),

  getOutwardTransactions: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`${API_BASE_URL}/outward${query ? `?${query}` : ''}`).then((res) => res.json());
  },

  getOutwardTransactionById: (id) =>
   authFetch(`${API_BASE_URL}/outward/${id}`).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Outward transaction not found");
      return data;
    }),

  bulkUploadOutwardExcel: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return authFetch(`${API_BASE_URL}/outward/bulk-upload`, {
      method: "POST",
      body: formData,
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // If response has errors array, include it in the error
        const errorObj = new Error(data.error || (Array.isArray(data.details) ? data.details.join("; ") : "Upload failed"));
        errorObj.responseData = data; // Attach full response data
        throw errorObj;
      }
      return data;
    });
  },

  updateOutwardTransaction: (id, data) =>
   authFetch(`${API_BASE_URL}/outward/${id}/edit`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to update outward transaction",
        );
      }
      return responseData;
    }),

  updateOutwardStatus: (id, data) =>
   authFetch(`${API_BASE_URL}/outward/${id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to update outward status",
        );
      }
      return responseData;
    }),

  updateOutwardItemStatus: (transactionId, itemId, data) =>
   authFetch(`${API_BASE_URL}/outward/${transactionId}/items/${itemId}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to update outward item status",
        );
      }
      return responseData;
    }),

  updateOutwardTransactionAllItemsStatus: (transactionId, data) =>
   authFetch(`${API_BASE_URL}/outward/transaction/${transactionId}/all-items/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to update outward transaction all items status",
        );
      }
      return responseData;
    }),

  updateOutwardItem: (transactionId, itemId, data) =>
   authFetch(`${API_BASE_URL}/outward/${transactionId}/items/${itemId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to update outward item",
        );
      }
      return responseData;
    }),

  dispatchOutwardTransaction: (id) =>
   authFetch(`${API_BASE_URL}/outward/${id}/dispatch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(responseData.error || "Failed to dispatch transaction");
      }
      return responseData;
    }),

  getOutwardHistoryReport: (filters) => {
    const params = new URLSearchParams(filters).toString();
    return authFetch(`${API_BASE_URL}/reports/outward-history?${params}`).then(
      (res) => res.json(),
    );
  },

  deleteOutwardTransaction: (id) =>
   authFetch(`${API_BASE_URL}/outward/${id}`, { method: "DELETE" }).then(
      async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Failed to delete outward transaction");
        return data;
      }
    ),

  deleteOutwardTransactionItem: (transactionId, itemId) =>
   authFetch(`${API_BASE_URL}/outward/${transactionId}/items/${itemId}`, { method: "DELETE" }).then(
      async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Failed to delete outward transaction item");
        return data;
      }
    ),

  getFEFORecommendation: (productId, requiredQuantity) =>
   authFetch(
      `${API_BASE_URL}/fefo-recommendation?productId=${productId}&requiredQuantity=${requiredQuantity}`,
    ).then((res) => res.json()),

  getBatchesByProduct: (productId) =>
   authFetch(`${API_BASE_URL}/batches-by-product?productId=${productId}`).then(
      (res) => res.json(),
    ),

  getApprovedPutawayProducts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`${API_BASE_URL}/outward/approved-putaway-products${query ? `?${query}` : ""}`).then((res) =>
      res.json()
    );
  },

  getNextIndentReference: () =>
   authFetch(`${API_BASE_URL}/next-indent-reference`).then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to get next indent reference');
      return data;
    }),

  downloadOutwardExcel: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await authFetch(`${API_BASE_URL}/outward/outward-download${query ? `?${query}` : ""}`, {
      method: "GET",
    });
    if (!res.ok) throw new Error("Failed to export outward excel");
    return res.blob();
  },
  downloadPicklist: async (outwardId) => {
   const res = await authFetch(
     `${API_BASE_URL}/outward/${outwardId}/picklist`,
     {
       method: "GET",
     }
   );
 
   if (!res.ok) {
     const data = await res.json();
     throw new Error(data.error || "Failed to download picklist");
   }
 
   return res.blob();
 },
};

// Return Services
export const returnService = {
  getOutwardInvoicesForReturn: () =>
   authFetch(`${API_BASE_URL}/returns/outward-invoices`).then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch outward invoices");
      return data;
    }),

  createReturnTransaction: (data) =>
   authFetch(`${API_BASE_URL}/returns`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then((res) => res.json()),

  getReturnTransactions: (filters) => {
    const params = new URLSearchParams(filters).toString();
    return authFetch(`${API_BASE_URL}/returns?${params}`).then((res) => res.json());
  },

  getReturnTransactionById: (id) =>
   authFetch(`${API_BASE_URL}/returns/${id}`).then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Return transaction not found");
      return data;
    }),

  updateReturnTransaction: (id, data) =>
   authFetch(`${API_BASE_URL}/returns/${id}/edit`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to update return transaction",
        );
      }
      return responseData;
    }),

  updateReturnStatus: (id, data) =>
   authFetch(`${API_BASE_URL}/returns/${id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to update return status",
        );
      }
      return responseData;
    }),

  approveReturnTransaction: (id) =>
   authFetch(`${API_BASE_URL}/returns/${id}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }).then((res) => res.json()),

  deleteReturnTransaction: (id) =>
   authFetch(`${API_BASE_URL}/returns/${id}`, { method: "DELETE" }).then(
      async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Failed to delete return transaction");
        return data;
      }
    ),

  rejectReturnTransaction: (id) =>
   authFetch(`${API_BASE_URL}/returns/${id}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }).then((res) => res.json()),
};

// Putaway Services
export const putawayService = {
  getPutawayTasks: (filters) => {
    const params = new URLSearchParams(filters).toString();
    return authFetch(`${API_BASE_URL}/putaway?${params}`).then((res) => res.json());
  },

  getPutawayTaskById: (id) =>
   authFetch(`${API_BASE_URL}/putaway/${id}`).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to fetch putaway details');
      return data;
    }),

  getBinsAvailableForPutaway: (putawayId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`${API_BASE_URL}/putaway/${putawayId}/bins-available${query ? `?${query}` : ''}`).then((res) => res.json());
  },

  updatePutawayStatus: (id, data) =>
   authFetch(`${API_BASE_URL}/putaway/${id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to update putaway status",
        );
      }
      return responseData;
    }),

  completePutaway: (id, data) =>
   authFetch(`${API_BASE_URL}/putaway/${id}/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(body.error || "Failed to complete putaway");
        err.details = body;
        throw err;
      }
      return body;
    }),

  getPendingPutawayCount: () =>
   authFetch(`${API_BASE_URL}/putaway/pending/count`).then((res) => res.json()),

  assignPutawayBins: (id, body) =>
   authFetch(`${API_BASE_URL}/putaway/${id}/assign-bins`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data.error || "Failed to assign bins");
        err.details = data;
        throw err;
      }
      return data;
    }),
};

// Bin Services
export const binService = {
  createBin: (data) =>
   authFetch(`${API_BASE_URL}/bins`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const json = await res.json();
      if (!res.ok) {
        const err = new Error(json?.error || json?.message || "Failed to create bin");
        err.response = { data: json };
        throw err;
      }
      return json;
    }),

  getBins: (filters) => {
    const params = new URLSearchParams(filters).toString();
    return authFetch(`${API_BASE_URL}/bins?${params}`).then((res) => res.json());
  },

  getBinById: (id) =>
   authFetch(`${API_BASE_URL}/bins/${id}`).then((res) => res.json()),

  updateBin: (id, data) =>
   authFetch(`${API_BASE_URL}/bins/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        const errorMsg = responseData.error || "Failed to update bin";
        throw new Error(errorMsg);
      }
      return responseData;
    }),

  updateBinStatus: (id, data) =>
   authFetch(`${API_BASE_URL}/bins/${id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error(
          responseData.error || "Failed to update bin status",
        );
      }
      return responseData;
    }),

  getBinCapacityStatus: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return authFetch(`${API_BASE_URL}/bins/capacity/status${params ? `?${params}` : ''}`).then((res) => res.json());
  },

  getBinBatches: (id) =>
   authFetch(`${API_BASE_URL}/bins/${id}/batches`).then(async (res) => {
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to fetch bin batches');
      }
      return data;
    }),

  getProductsForBin: (id) =>
   authFetch(`${API_BASE_URL}/bins/${id}/products`).then(async (res) => {
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to fetch products for bin');
      }
      return data;
    }),

  deleteBin: (id, forceDelete = false) =>
   authFetch(`${API_BASE_URL}/bins/${id}${forceDelete ? '?forceDelete=true' : ''}`, { method: "DELETE" }).then(
      async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const error = new Error(data.error || "Failed to delete bin");
          error.batchesCount = data.batchesCount;
          throw error;
        }
        return data;
      }
    ),

  moveBatchBetweenBins: (data) =>
   authFetch(`${API_BASE_URL}/bins/move-batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }).then((res) => res.json()),
};

// Dashboard Services
export const dashboardService = {
  getDashboardOverview: (params) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : "";
    return authFetch(`${API_BASE_URL}/dashboard/overview${query}`).then((res) =>
      res.json()
    );
  },
};

// Report Services
export const reportService = {
  getInventoryReportByProduct: (paramsOrCompanyId) => {
    const params = typeof paramsOrCompanyId === 'object' && paramsOrCompanyId !== null
      ? paramsOrCompanyId
      : { companyId: paramsOrCompanyId };
    const url = new URL(`${API_BASE_URL}/reports/inventory/product`);
    if (params.companyId) url.searchParams.append('companyId', params.companyId);
    if (params.page) url.searchParams.append('page', params.page);
    if (params.limit) url.searchParams.append('limit', params.limit);
    if (params.search) url.searchParams.append('search', params.search);
    if (params.startDate) url.searchParams.append('startDate', params.startDate);
    if (params.endDate) url.searchParams.append('endDate', params.endDate);
    return authFetch(url.toString()).then((res) => res.json());
  },

  getInventoryReportByBatch: (paramsOrCompanyId) => {
    const params = typeof paramsOrCompanyId === 'object' && paramsOrCompanyId !== null
      ? paramsOrCompanyId
      : { companyId: paramsOrCompanyId };
    const url = new URL(`${API_BASE_URL}/reports/inventory/batch`);
    if (params.companyId) url.searchParams.append('companyId', params.companyId);
    if (params.page) url.searchParams.append('page', params.page);
    if (params.limit) url.searchParams.append('limit', params.limit);
    if (params.search) url.searchParams.append('search', params.search);
    if (params.startDate) url.searchParams.append('startDate', params.startDate);
    if (params.endDate) url.searchParams.append('endDate', params.endDate);
    return authFetch(url.toString()).then((res) => res.json());
  },

  getInventoryReportByUOM: (companyId) => {
    const url = new URL(`${API_BASE_URL}/reports/inventory/uom`);
    if (companyId) url.searchParams.append('companyId', companyId);
    return authFetch(url.toString()).then((res) => res.json());
  },

  getInventorySummary: (companyId) => {
    const url = new URL(`${API_BASE_URL}/reports/inventory/summary`);
    if (companyId) url.searchParams.append('companyId', companyId);
    return authFetch(url.toString()).then((res) => res.json());
  },

  getAgingReport: (paramsOrCompanyId) => {
    const params = typeof paramsOrCompanyId === 'object' && paramsOrCompanyId !== null
      ? paramsOrCompanyId
      : { companyId: paramsOrCompanyId };
    const url = new URL(`${API_BASE_URL}/reports/aging`);
    if (params.companyId) url.searchParams.append('companyId', params.companyId);
    if (params.page) url.searchParams.append('page', params.page);
    if (params.limit) url.searchParams.append('limit', params.limit);
    if (params.search) url.searchParams.append('search', params.search);
    if (params.startDate) url.searchParams.append('startDate', params.startDate);
    if (params.endDate) url.searchParams.append('endDate', params.endDate);
    return authFetch(url.toString()).then((res) => res.json());
  },

  // Download report methods
  downloadInventoryReportByProduct: (companyId) => {
    const url = new URL(`${API_BASE_URL}/reports/download/inventory/product`);
    if (companyId) url.searchParams.append('companyId', companyId);
    return authFetch(url).then((res) => {
      if (!res.ok) throw new Error('Failed to download report');
      return res.blob();
    });
  },

  downloadInventoryReportByBatch: (companyId) => {
    const url = new URL(`${API_BASE_URL}/reports/download/inventory/batch`);
    if (companyId) url.searchParams.append('companyId', companyId);
    return authFetch(url).then((res) => {
      if (!res.ok) throw new Error('Failed to download report');
      return res.blob();
    });
  },

  downloadInventoryReportByUOM: (companyId) => {
    const url = new URL(`${API_BASE_URL}/reports/download/inventory/uom`);
    if (companyId) url.searchParams.append('companyId', companyId);
    return authFetch(url).then((res) => {
      if (!res.ok) throw new Error('Failed to download report');
      return res.blob();
    });
  },

  downloadInventorySummary: (companyId) => {
    const url = new URL(`${API_BASE_URL}/reports/download/inventory/summary`);
    if (companyId) url.searchParams.append('companyId', companyId);
    return authFetch(url).then((res) => {
      if (!res.ok) throw new Error('Failed to download report');
      return res.blob();
    });
  },

  downloadAgingReport: (companyId) => {
    const url = new URL(`${API_BASE_URL}/reports/download/aging`);
    if (companyId) url.searchParams.append('companyId', companyId);
    return authFetch(url).then((res) => {
      if (!res.ok) throw new Error('Failed to download report');
      return res.blob();
    });
  },
};
